/**
 * Email-HTML primitives — ported VERBATIM from the legacy monolith
 * (legacy/smart-email-request/index.html: esc, AR, btnHTML, __theme, __inset).
 * Do not "modernize" the emitted markup: every attribute exists because some
 * email client needs it (mso-padding-alt for Outlook, role="presentation",
 * table-based buttons, etc.).
 */

/** the email-safe font stack used across every block */
export const AR = "Arial,Helvetica,sans-serif";

/**
 * HTML-escape for TEXT nodes — byte-identical to the monolith's esc() and
 * therefore load-bearing for the 53/53 render-parity gate: do NOT add quote
 * escaping here, it would change every rendered block's bytes.
 * For values that land inside an ATTRIBUTE, use escAttr().
 */
/**
 * esc() must escape QUOTES, not just angle brackets (ported from production 2026-08-17).
 *
 * Production's esc() gained quote escaping on 2026-08-14 after a fuzz run: esc() is used at
 * ~295 sites in the monolith and many interpolate into an ATTRIBUTE (src=, href, alt, width).
 * With only `<`, `>` and `&` escaped, a value containing a double quote closes the attribute
 * early and everything after it parses as new attributes — a hero `src` of
 *   ZQ"><b onmouseover="1">ZQ
 * rendered as <img src="ZQ" … onmouseover="1" …>, a live event handler. 29 of 72 blocks were
 * breakable that way.
 *
 * Escaping quotes is correct in BOTH contexts — a text node renders `&quot;` / `&#39;` as the
 * literal character — and this brought the last 9 parity mismatches to zero: the monolith
 * emits `&#39;` in body copy ("Everything&#39;s Included®", "We&#39;re Here to Help You")
 * where this app was emitting a raw apostrophe.
 *
 * `escAttr` below is now redundant for quotes but harmless (escaping is idempotent here),
 * so it is left in place rather than churning its ~call sites in one change.
 */
export function esc(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Attribute-safe escape (CodeQL "Incomplete HTML attribute sanitization",
 * PR #19): esc() leaves quotes intact, so a value containing a double quote
 * could close a "…"-delimited attribute and inject markup. Use this for every
 * interpolation inside href/src/alt/style/title. Superset of esc(), so text
 * stays correct too; parity-safe because the monolith's own attribute values
 * never contain quotes (this only changes output for hostile input).
 */
export function escAttr(s: unknown): string {
  return esc(s).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * Multi-line-safe escape — monolith `escML` (L7964):
 *   `function escML(s){return esc(s).replace(/\r\n|\r|\n/g,"<br>");}`
 *
 * SFMC block fields are textareas, and `esc()` leaves a bare "\n" in the markup
 * which HTML collapses to a space (monolith note, 2026-08-14). Every ECH block's
 * token substitution goes through this, not `esc`.
 */
export function escML(s: unknown): string {
  return esc(s).replace(/\r\n|\r|\n/g, "<br>");
}

/** bulletproof button (Outlook mso-padding-alt) — legacy btnHTML */
/**
 * Button palettes — legacy PAL_NAVY / PAL_OUTLINE.
 *
 * NB "outline" is deliberately NOT one of the seven named Brand Standards styles: the
 * Studio's outline is a white fill with a navy border, whereas §1.3's Outlined Navy Pill is
 * a #1B2A58 fill with a #303030 border. The monolith preserves the existing APPEARANCE
 * rather than silently restyling a division's button, so this port does too.
 */
export interface BtnPalette { fill: string; border: string; text: string; radius: number; }
export const PAL_NAVY: BtnPalette = { fill: "#1B2A58", border: "#1B2A58", text: "#ffffff", radius: 32 };
export const PAL_OUTLINE: BtnPalette = { fill: "#ffffff", border: "#1B2A58", text: "#1B2A58", radius: 32 };

/** §1.3 fixed CTA widths — label pattern → [px, pt]. Legacy CTA_W. */
const CTA_W: [RegExp, number, number][] = [
  [/^see more homes\b/i, 280, 200],
  [/^view homes of the week$/i, 240, 180],
  [/^(schedule a tour|contact a consultant)$/i, 230, 165],
  [/^talk to a specialist$/i, 220, 155],
  [/^get directions$/i, 200, 140],
];
/** legacy __ctaW */
export function ctaW(label?: string): { px: number; pt: number } {
  const s = String(label == null ? "" : label).trim();
  for (const [re, px, pt] of CTA_W) if (re.test(s)) return { px, pt };
  return { px: 200, pt: 130 };   /* §1.3 Navy Pill — universal default */
}

/**
 * The bulletproof button — legacy __btnMarkup. PORTED 2026-08-17.
 *
 * This was missing from the port and accounted for 13 of the parity mismatches once the
 * gate's vendored reference was refreshed to the live monolith — the single largest source
 * of drift between the CloudPage tool and this app. The previous btnHTML emitted only an
 * HTML table with `mso-padding-alt`, which Outlook renders as a square, mis-sized button;
 * production moved to the VML pattern and the port never followed.
 *
 * It went unnoticed because tests/parity/legacy-index.html was a pre-2026-08-14 snapshot, so
 * the gate was comparing this app against a monolith that still had the old button — green,
 * and certifying the wrong thing.
 *
 * Outlook sees ONLY the VML (the HTML table is wrapped in `[if !mso]`). Every other client
 * sees only the table. That is the bulletproof pattern the standard names.
 */
export function btnMarkup(label: string, url: string, pal: BtnPalette): string {
  const w = ctaW(label), href = esc(url || "#"), tx = esc(label || "Button");
  const border = pal.border === "none" ? "0" : "2px solid " + pal.border;
  const vml = '<!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="' + href
    + '" style="height:39pt;v-text-anchor:middle;width:' + w.pt + 'pt;mso-position-horizontal:center" arcsize="62%" fillcolor="' + pal.fill
    + '" strokecolor="' + pal.border + '" strokeweight="1pt"><w:anchorlock/><center style="color:' + pal.text
    + ";font-family:" + AR + ';font-size:14px;font-weight:bold">' + tx + "</center></v:roundrect><![endif]-->";
  const tbl = '<!--[if !mso]><!--><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:6px auto;border-collapse:separate;line-height:100%"><tr><td align="center" bgcolor="' + pal.fill
    + '" valign="middle" width="' + w.px + '" style="width:' + w.px + "px;border:" + border + ";border-radius:" + pal.radius + "px;background:" + pal.fill
    + ';cursor:auto"><a href="' + href + '" target="_blank" style="display:block;font-family:' + AR
    + ";font-size:14px;font-weight:bold;color:" + pal.text + ";line-height:120%;padding:17px 25px;text-decoration:none;border-radius:" + pal.radius + 'px">' + tx
    + "</a></td></tr></table><!--<![endif]-->";
  return vml + tbl;
}

/** legacy btnHTML — a thin style→palette selector over btnMarkup */
export function btnHTML(label: string, url: string, style?: "filled" | "outline"): string {
  return btnMarkup(label, url, style !== "outline" ? PAL_NAVY : PAL_OUTLINE);
}

export interface ThemeSpec {
  band: string;
  bandTx: string;
  view: string;
  btn: string;
  border: string;
}

/** color themes for bands/buttons — White / Midnight Blue / Cobalt Blue (legacy __theme) */
export function theme(t?: string): ThemeSpec {
  if (t === "cobalt")
    return { band: "#2C6BBD", bandTx: "#ffffff", view: "#ffffff", btn: "#2C6BBD", border: "" };
  if (t === "white")
    return {
      band: "#ffffff",
      bandTx: "#1B365D",
      view: "#1B365D",
      btn: "#1B2A58",
      border: ";border-bottom:2px solid #1B365D",
    };
  return { band: "#1B2A58", bandTx: "#ffffff", view: "#ffffff", btn: "#1B2A58", border: "" };
}

export interface InsetSpec {
  pad: string;
  w: number;
}

/** image inset: full-bleed 600 vs 20px / 50px padded frame (legacy __inset) */
export function inset(v?: string): InsetSpec {
  if (v === "20") return { pad: "0 20px", w: 560 };
  if (v === "50") return { pad: "0 50px", w: 500 };
  return { pad: "0", w: 600 };
}
